/**
 * CORE DRAG & DROP LOGIC
 *
 * ВНИМАНИЕ: Здесь содержится сложная логика расчета координат для
 * Grid/Flex/Absolute перемещений.
 *
 * Основные проблемы/TODO для будущих поколений:
 * 1. Функция createGetResult слишком большая, стоит разбить по стратегиям (Layout Strategy).
 * 2. Оптимизировать getComputedStyle внутри цикла (Layout Thrashing).
 * 3. Логика Guides переусложнена в reduce.
 * 4. И тд и тп.
 *
 * Если будете править — аккуратнее с расчетами Grid Gap.
 */
import type { LayoutNode } from '@vedalib/editor'
import { isShellNode, notEmpty, type Block, type NodeV2 } from '@vedalib/editor'
import type { EditorElement } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'

import {
  getDeepChildrenIds,
  getNodeSafe,
  getParentNode,
} from 'components/Editor/EditorContext/selectors/block'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import {
  dragEnd,
  dragMove,
  dragStart,
  updateBlock,
  setProjectNavigation,
  setCommentForm,
} from 'services/Store/Project/actions'
import { AppMode, DeviceMode } from 'services/Store/Project/enums'
import {
  moveNode,
  resetStyle,
  updateCoordinates,
  setGridArea,
  setHeight,
  setWidth,
} from 'services/Store/Project/mutators/node'
import {
  getBlock,
  getDeviceMode,
  getNodeId,
  getParents,
  getPathNodes,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { IProjectContext, MapType } from 'services/Store/Project/types'
import store from 'services/Store/store'
import { isCtrl } from 'utils/events'

import type { ICoordinates, IFullCoord, IGuide } from '../DisplayProvider/types'
import {
  ABSOLUTE_OFFSET,
  calcAbsoluteGuides,
  collectDndData,
  findNodeRef,
  isOffset,
  remapNodesRelativeToArea,
} from '../DisplayProvider/utils'
import type { IGridArea } from '../formControls/Area/types'
import { safeParseArea } from '../formControls/Area/utils'
import { safeParseCoords } from '../formControls/Coords/utils'
import { safeParseGrid } from '../formControls/Grid/utils'
import { safeParsePxSizes } from '../formControls/Sizes/utils'
import { isGrid, isRoot } from '../guards'

const getFullArea = (styles: IGridArea[]) =>
  styles.reduce(
    (acc, { rowStart, colStart, rowEnd, colEnd }) => ({
      rowStart: Math.min(acc.rowStart, rowStart),
      colStart: Math.min(acc.colStart, colStart),
      rowEnd: Math.max(acc.rowEnd, rowEnd),
      colEnd: Math.max(acc.colEnd, colEnd),
    }),
    { rowStart: Infinity, colStart: Infinity, rowEnd: -Infinity, colEnd: -Infinity },
  )

const createGetResult = (
  event: React.MouseEvent,
  box: Omit<DOMRect, 'toJSON'>,
  state: IProjectContext,
  blockId: string | null,
  source: NodeV2 | null,
  targets: NodeV2[],
  targetRefs: { id: string; rect?: Omit<DOMRect, 'toJSON'> | null }[],
) => {
  const initialCoords: ICoordinates = { x: event.clientX, y: event.clientY }
  const block = getBlock(state, { id: blockId })
  const device = getDeviceMode(state)
  const sourceId = source?.id ?? null
  const sourceStyle = mergeNodeStyle(source?.style || {}, device)
  const targetStyles = targets.map((node) => mergeNodeStyle(node.style, device))
  const allGuides: Record<string, IGuide[]> = {}
  const sourceRef = sourceId && findNodeRef(sourceId)
  const computedStyle = sourceRef && getComputedStyle(sourceRef)
  const initialFullBox = targetRefs.length
    ? {
        top: Math.min(...targetRefs.map((item) => item.rect?.top || 0)),
        left: Math.min(...targetRefs.map((item) => item.rect?.left || 0)),
        bottom: Math.max(...targetRefs.map((item) => item.rect?.bottom || 0)),
        right: Math.max(...targetRefs.map((item) => item.rect?.right || 0)),
      }
    : box

  return (
    data: ReturnType<typeof collectDndData>,
  ): {
    index: number | null
    areas: MapType<IGridArea> | null
    coords: MapType<IFullCoord> | null
    flex: { x: number; y: number } | null
    guides: IGuide[] | null
  } => {
    if (!block) {
      return { areas: null, index: null, coords: null, flex: null, guides: null }
    }
    const { destinationId, row, col, insertIndex = null } = data
    const destNode = block.schema.nodes[destinationId || ''] as NodeV2 | undefined
    const destRef = destinationId ? findNodeRef(destinationId) : null
    const destStyle = mergeNodeStyle(destNode?.style || {}, getDeviceMode(state))
    const isSameParent = sourceId === destinationId

    if (destStyle.display === 'grid' && R.isNotNil(row) && R.isNotNil(col)) {
      const destinationCols = safeParseGrid(destStyle?.gridColumns).items.length
      const targetAreas = isGrid(sourceStyle)
        ? targetStyles.map((style) => safeParseArea(style?.gridArea))
        : targetStyles.map((_item, index) => ({
            rowStart: index,
            colStart: 0,
            rowEnd: index + 1,
            colEnd: R.clamp(2, Math.max(2, destinationCols), Math.floor((destinationCols - 2) / 2)),
          }))

      const initialFullArea = getFullArea(targetAreas)

      const { gridTemplateRows, gridTemplateColumns, gap } = computedStyle || {}
      const rows = !isSameParent
        ? (Array.from({ length: 100 }).fill(
            (initialFullBox.bottom - initialFullBox.top) / 100,
          ) as number[])
        : gridTemplateRows
            ?.split(' ')
            .map(parseFloat)
            ?.slice(initialFullArea.rowStart - 1, initialFullArea.rowEnd - 1) || []
      const cols: number[] = !isSameParent
        ? (Array.from({ length: 100 }).fill(
            (initialFullBox.right - initialFullBox.left) / 100,
          ) as number[])
        : gridTemplateColumns
            ?.split(' ')
            .map(parseFloat)
            ?.slice(initialFullArea.colStart - 1, initialFullArea.colEnd - 1) || []

      const [rowGaps, columnGaps = rowGaps] = gap?.split(' ').map((val) => parseFloat(val)) || []
      const deltaY = initialCoords.y - (initialFullBox.top || 0)
      const deltaX = initialCoords.x - (initialFullBox.left || 0)

      const deltaRow = (() => {
        let offset = 0
        for (let i = 0; i < rows.length; i++) {
          const gap = !isSameParent ? 0 : rowGaps || 0
          offset += rows[i] + gap
          if (offset > deltaY) {
            return i
          }
        }
        return rows.length - 1
      })()

      const deltaCol = (() => {
        let offset = 0
        for (let i = 0; i < cols.length; i++) {
          const gap = !isSameParent ? 0 : columnGaps || 0
          offset += cols[i] + gap
          if (offset > deltaX) {
            return i
          }
        }
        return cols.length - 1
      })()

      const initialCell = {
        row: isSameParent ? deltaRow : Math.floor((deltaRow / rows.length) * targets.length),
        col: isSameParent
          ? deltaCol
          : Math.floor(
              (deltaCol / cols.length) *
                R.clamp(2, Math.max(2, destinationCols), Math.floor((destinationCols - 2) / 2)),
            ),
      }

      const resultFullArea = isSameParent
        ? {
            rowStart: (row || 0) - (initialCell?.row || 0),
            colStart: (col || 0) - (initialCell?.col || 0),
            rowEnd:
              (row || 0) +
              (initialFullArea.rowEnd - initialFullArea.rowStart) -
              (initialCell?.row || 0),
            colEnd:
              (col || 0) +
              (initialFullArea.colEnd - initialFullArea.colStart) -
              (initialCell?.col || 0),
          }
        : {
            rowStart: (row || 0) - (initialCell?.row || 0),
            colStart: (col || 0) - (initialCell?.col || 0),
            rowEnd: (row || 0) + targets.length - (initialCell?.row || 0),
            colEnd:
              (col || 0) +
              R.clamp(2, Math.max(2, destinationCols), Math.floor((destinationCols - 2) / 2)) -
              (initialCell?.col || 0),
          }

      const areas = remapNodesRelativeToArea(targetAreas, initialFullArea, resultFullArea)

      return {
        areas: targets.reduce((acc, node, idx) => {
          acc[node.id] = areas[idx]
          return acc
        }, {} as MapType<IGridArea>),
        index: insertIndex,
        flex: null,
        coords: null,
        guides: null,
      }
    }

    if (
      (destStyle.display === 'absolute' ||
        (destStyle.display === 'flex' &&
          targetStyles.every((style) => style.flexFlow === 'absolute'))) &&
      destRef &&
      destinationId
    ) {
      const destBox = destRef.getBoundingClientRect()
      const guides = (() => {
        if (destinationId && allGuides[destinationId]) {
          return allGuides[destinationId]
        } else if (destNode) {
          const newGuides = calcAbsoluteGuides(
            destNode,
            block,
            device,
            destBox,
            destStyle,
            targets.map((t) => t.id),
          )
          allGuides[destinationId] = newGuides
          return newGuides
        }
        return EMPTY_ARRAY
      })()
      const { insertIndex = null, top: y = 0, left: x = 0 } = data

      const horizontalGuides = guides.filter((g) => g.y1 === g.y2)
      const verticalGuides = guides.filter((g) => g.x1 === g.x2)
      const activeGuides = guides.filter((g) => g.alwaysShow) || []

      const offsets = safeParsePxSizes(destStyle.margin)
      const topOffset = offsets[0] || 0
      // const rightOffset = offsets[1] || 0
      // const bottomOffset = offsets[2] || 0
      const leftOffset = offsets[3] || 0

      const coords = targets.reduce((acc, node, index) => {
        const style = targetStyles[index]
        const coords = safeParseCoords(style?.coords)
        const margins = safeParsePxSizes(style.margin)
        const topMargin = margins[0] || 0
        const rightMargin = margins[1] || 0
        const bottomMargin = margins[2] || 0
        const leftMargin = margins[3] || 0
        const rect = targetRefs.find((n) => n.id === node.id)?.rect
        const height = rect?.height || box.height
        const width = rect?.width || box.width
        const delta = {
          top:
            (rect?.top || 0) -
            (targetRefs.length ? Math.min(...targetRefs.map((item) => item.rect?.top || 0)) : 0),
          left:
            (rect?.left || 0) -
            (targetRefs.length ? Math.min(...targetRefs.map((item) => item.rect?.left || 0)) : 0),
        }
        const y1 = y - (initialCoords.y - box.top) + delta.top - topMargin
        const yMid = y1 + (topMargin + height + bottomMargin) / 2
        const y2 = y1 + topMargin + height + bottomMargin
        const x1 = x - (initialCoords.x - box.left) + delta.left - leftMargin
        const xMid = x1 + (leftMargin + width + rightMargin) / 2
        const x2 = x1 + leftMargin + width + rightMargin

        const {
          left,
          right,
          guides: vGuides,
        } = verticalGuides.reduce(
          (nearest, g) => {
            const minX1 = Math.abs(g.x1 - leftOffset - x1)
            const minXMid = Math.abs(g.x1 - leftOffset - xMid)
            const minX2 = Math.abs(g.x1 - leftOffset - x2)
            const min = Math.min(minX1, minXMid, minX2)
            const isMin = min < nearest.dist
            return {
              ...nearest,
              ...(isMin && {
                dist: min,
                ...(min === minX1 && {
                  left: min < ABSOLUTE_OFFSET ? g.x1 - leftOffset : x1,
                  right: undefined,
                  guides: [
                    {
                      ...g,
                      helper:
                        min >= ABSOLUTE_OFFSET
                          ? {
                              y1: topOffset + y1 + height / 2,
                              y2: topOffset + y1 + height / 2,
                              x1: g.x1,
                              x2: leftOffset + x1,
                              text: min,
                            }
                          : null,
                    },
                  ],
                }),
                ...(min === minXMid && {
                  left:
                    min < ABSOLUTE_OFFSET
                      ? g.x1 - leftOffset - (leftMargin + width + rightMargin) / 2
                      : x1,
                  right: undefined,
                  guides: [
                    {
                      ...g,
                      helper:
                        min >= ABSOLUTE_OFFSET
                          ? {
                              y1: topOffset + y1 + height / 2,
                              y2: topOffset + y1 + height / 2,
                              x1: g.x1,
                              x2: leftOffset + x2,
                              text: min,
                            }
                          : null,
                    },
                  ],
                }),
                ...(min === minX2 && {
                  left: undefined,
                  right:
                    min < ABSOLUTE_OFFSET ? destBox.width + leftOffset - g.x1 : destBox.width - x2,
                  guides: [
                    {
                      ...g,
                      helper:
                        min >= ABSOLUTE_OFFSET
                          ? {
                              y1: topOffset + y1 + height / 2,
                              y2: topOffset + y1 + height / 2,
                              x1: g.x1,
                              x2: leftOffset + x2,
                              text: min,
                            }
                          : null,
                    },
                  ],
                }),
              }),
            }
          },
          {
            left: coords.left || (!coords.left && !coords.right) ? x1 : undefined,
            right: coords.right ? destBox.width - x2 : undefined,
            dist: Infinity,
            guides: [],
          } as { left?: number; right?: number; dist: number; guides: IGuide[] },
        )

        const {
          top,
          bottom,
          guides: hGuides,
        } = horizontalGuides.reduce(
          (nearest, g) => {
            const minY1 = Math.abs(g.y1 - topOffset - y1)
            const minYMid = Math.abs(g.y1 - topOffset - yMid)
            const minY2 = Math.abs(g.y1 - topOffset - y2)
            const min = Math.min(minY1, minYMid, minY2)
            const isMin = min < nearest.dist
            return {
              ...nearest,
              ...(isMin && {
                dist: min,
                ...(min === minY1 && {
                  top: min < ABSOLUTE_OFFSET ? g.y1 - topOffset : y1,
                  bottom: undefined,
                  guides: [
                    {
                      ...g,
                      helper:
                        min >= ABSOLUTE_OFFSET
                          ? {
                              x1: leftOffset + x1 + width / 2,
                              x2: leftOffset + x1 + width / 2,
                              y1: g.y1,
                              y2: topOffset + y1,
                              text: min,
                            }
                          : null,
                    },
                  ],
                }),
                ...(min === minYMid && {
                  top:
                    min < ABSOLUTE_OFFSET
                      ? g.y1 - topOffset - (topMargin + height + bottomMargin) / 2
                      : y1,
                  bottom: undefined,
                  guides: [
                    {
                      ...g,
                      helper:
                        min >= ABSOLUTE_OFFSET
                          ? {
                              x1: leftOffset + x1 + width / 2,
                              x2: leftOffset + x1 + width / 2,
                              y1: g.y1,
                              y2: topOffset + y1,
                              text: min,
                            }
                          : null,
                    },
                  ],
                }),
                ...(min === minY2 && {
                  top: undefined,
                  bottom:
                    min < ABSOLUTE_OFFSET ? destBox.height + topOffset - g.y1 : destBox.height - y2,
                  guides: [
                    {
                      ...g,
                      helper:
                        min >= ABSOLUTE_OFFSET
                          ? {
                              x1: leftOffset + x1 + width / 2,
                              x2: leftOffset + x1 + width / 2,
                              y1: g.y1,
                              y2: topOffset + y2,
                              text: min,
                            }
                          : null,
                    },
                  ],
                }),
              }),
            }
          },
          {
            top: coords.top || (!coords.top && !coords.bottom) ? y1 : undefined,
            bottom: coords.bottom ? destBox.height - y2 : undefined,
            dist: Infinity,
            guides: [],
          } as { top?: number; bottom?: number; dist: number; guides: IGuide[] },
        )

        activeGuides.unshift(...hGuides, ...vGuides)

        acc[node.id] = {
          top,
          left,
          right,
          bottom,
          width: rect?.width || box.width,
          height: rect?.height || box.height,
          offset: [topMargin || 0, rightMargin || 0, bottomMargin || 0, leftMargin || 0],
        }
        return acc
      }, {} as MapType<IFullCoord>)

      return {
        coords,
        guides: R.uniqBy(({ id }) => id, activeGuides),
        index: insertIndex,
        areas: null,
        flex: null,
      }
    }

    if (destStyle.display === 'flex') {
      const { insertIndex = null, insertX = null, insertY = null } = data
      return {
        index: insertIndex,
        flex: { x: insertX ?? 0, y: insertY ?? 0 },
        areas: null,
        coords: null,
        guides: null,
      }
    }

    return { index: insertIndex, areas: null, coords: null, flex: null, guides: null }
  }
}

const getParent = (state: IProjectContext, blockId?: string | null, nodeId?: string | null) => {
  const currNodeId = getNodeId(state)
  const block = getBlock(state, { id: blockId })
  const current = getNodeSafe(block, currNodeId)
  const currentPath = (block && current?.id && getPathNodes(block, current.id)) || []
  const currentParent: LayoutNode | null = currentPath[currentPath.length - 2] || null
  const currentChildren = getDeepChildrenIds(block, currentParent?.id) || []

  return {
    parent: currentParent,
    isParent: currentParent && (currentChildren.includes(nodeId || '') || nodeId === current?.id),
  }
}

interface IUseDragArgs {
  mode: AppMode | null
  zone?: 'navigation' | 'canvas'
  blockId: string | null
  source: NodeV2 | null
  targets: NodeV2[]
  isContentPlus?: boolean
  nodeId?: string | null
  data?: { elements?: MapType<EditorElement>; nodes?: MapType<NodeV2> }
}

export const onDrag = (downEvent: React.MouseEvent, args: IUseDragArgs) => {
  const dispatch = store.dispatch

  const { zone = 'canvas', mode, blockId, nodeId, source, targets, data, isContentPlus } = args

  downEvent.stopPropagation()

  const initialCoords: ICoordinates = { x: downEvent.clientX, y: downEvent.clientY }
  const state = store.getState().project
  const device = getDeviceMode(state)
  const block = getBlock(state, { id: blockId })
  const sourceId = source?.id ?? null
  const sourceStyle = mergeNodeStyle(source?.style || {}, device)
  const targetRefs = targets
    .map((node) => ({
      id: node.id,
      rect: findNodeRef(node.id)?.getBoundingClientRect(),
    }))
    .filter(({ rect }) => Boolean(rect))

  const box = {
    ...(targetRefs.length
      ? {
          x: Math.min(...targetRefs.map(({ rect }) => rect?.x || Infinity)),
          y: Math.min(...targetRefs.map(({ rect }) => rect?.y || Infinity)),
          bottom: Math.max(...targetRefs.map(({ rect }) => rect?.bottom || 0)),
          right: Math.max(...targetRefs.map(({ rect }) => rect?.right || 0)),
          left: Math.min(...targetRefs.map(({ rect }) => rect?.left || Infinity)),
          top: Math.min(...targetRefs.map(({ rect }) => rect?.top || Infinity)),
          width:
            Math.max(...targetRefs.map(({ rect }) => rect?.right || 0)) -
            Math.min(...targetRefs.map(({ rect }) => rect?.left || Infinity)),
          height:
            Math.max(...targetRefs.map(({ rect }) => rect?.bottom || 0)) -
            Math.min(...targetRefs.map(({ rect }) => rect?.top || Infinity)),
        }
      : R.omit(['toJSON'], downEvent.currentTarget.getBoundingClientRect())),
    ...(zone === 'navigation' &&
      R.pick(['top', 'left', 'x', 'y'], downEvent.currentTarget.getBoundingClientRect())),
  }

  let raf: number | null = null
  let isDragging = false

  const targetIds = targets.map((node) => node.id)
  const getResult = createGetResult(downEvent, box, state, blockId, source, targets, targetRefs)

  const handleMouseMove = (moveEvent: MouseEvent) => {
    if (raf) {
      return
    }
    raf = requestAnimationFrame(() => {
      raf = null
      const selection = window.getSelection()
      const currentCoords = { x: moveEvent.clientX, y: moveEvent.clientY }

      if (
        block &&
        !isDragging &&
        targetIds.length &&
        targetIds.every((id) => !isRoot(block, block.schema.nodes[id])) &&
        (isContentPlus || mode === AppMode.pro) &&
        ((source && isRoot(block, source)) || !source?.isDone) &&
        isOffset(initialCoords, moveEvent) &&
        (device === DeviceMode.desktop ||
          sourceStyle.display === 'absolute' ||
          sourceStyle.display === 'grid')
      ) {
        isDragging = true
        selection?.removeAllRanges()
        dispatch(setProjectNavigation({ nodeIds: targetIds }))
        dispatch(dragStart({ type: 'DRAG', sourceId, initialCoords, targetIds, box }))
      }
      if (!isDragging || !initialCoords || !block) {
        return
      }

      const data = collectDndData(block, currentCoords)

      const { zone = null, destinationId = null } = data
      const { areas, flex, coords, guides, index } = getResult(data)

      dispatch(dragMove({ zone, destinationId, currentCoords, areas, coords, guides, flex, index }))
    })
  }

  const handleMouseUp = (upEvent: MouseEvent) => {
    const state = store.getState().project
    const dnd = state.state.editor.dnd
    if (block && dnd && !dnd.targetIds.includes(dnd.destinationId || '')) {
      upEvent.stopPropagation()
      const { zone, targetIds, sourceId, destinationId, areas, index, coords } = dnd
      const isCanvas = zone === 'canvas'
      const isNavigation = zone === 'navigation'
      const isTransfer = sourceId !== destinationId
      const destinationStyle = destinationId
        ? mergeNodeStyle((block.schema.nodes[destinationId] as NodeV2)?.style, device)
        : null
      const cbs = (targetIds.map((id) => {
        if (
          destinationStyle?.display === 'absolute' ||
          (destinationStyle?.display === 'flex' &&
            targets.every((node) => mergeNodeStyle(node.style, device).flexFlow === 'absolute'))
        ) {
          const coord = coords?.[id]
          const top = coord?.top
          const right = coord?.right
          const bottom = coord?.bottom
          const left = coord?.left
          const width = coord?.width
          const height = coord?.height

          return R.compose(
            resetStyle(id, device, 'gridArea'),
            isTransfer ? setWidth(id, device, width) : R.identity<Block>,
            isTransfer ? setHeight(id, device, height) : R.identity<Block>,
            isCanvas
              ? updateCoordinates(id, device, { left, top, right, bottom })
              : R.identity<Block>,
            moveNode(sourceId, destinationId, id, isNavigation ? index : null, data),
          )
        }

        if (destinationStyle?.display === 'flex') {
          return R.compose(
            resetStyle(id, device, 'coords'),
            resetStyle(id, device, 'gridArea'),
            // isTransfer ? resetStyle(id, device, 'width') : R.identity<Block>,
            // isTransfer ? resetStyle(id, device, 'height') : R.identity<Block>,
            moveNode(sourceId, destinationId, id, index, data),
          )
        }

        if (destinationStyle?.display === 'grid') {
          return R.compose(
            resetStyle(id, device, 'coords'),
            // isTransfer ? resetStyle(id, device, 'width') : R.identity<Block>,
            // isTransfer ? resetStyle(id, device, 'height') : R.identity<Block>,
            isCanvas && areas?.[id] ? setGridArea(id, device, areas[id]) : R.identity<Block>,
            moveNode(sourceId, destinationId, id, isNavigation ? index : null, data),
          )
        }

        return moveNode(sourceId, destinationId, id, index, data)
      }) || EMPTY_ARRAY) as [(block: Block) => Block]

      const value = R.compose(...cbs)(block)

      dispatch(updateBlock({ id: block.uuid, value }))
    } else if (nodeId !== undefined) {
      const shift = upEvent?.shiftKey
      const ctrl = isCtrl(upEvent)

      const state = store.getState().project
      const block = getBlock(state, { id: blockId })
      const node = getNodeSafe(block, nodeId)
      const parentIds = getParents(state, blockId, nodeId).map(({ id }) => id)
      const path = [...parentIds, nodeId].filter(notEmpty)
      const selectedNodes = state.state.editor.selectedNodes
      const curNode = selectedNodes[0]
      const curParent = block && curNode ? getParentNode(curNode, block)?.id : null
      const index = path.findIndex((id) => id === curParent)
      const nodeIdWithSameParent = ~index ? path[index + 1] : null

      if ((mode === AppMode.fill || mode === AppMode.pro) && ctrl && nodeIdWithSameParent) {
        const nodeIds = R.symmetricDifference(selectedNodes, [nodeIdWithSameParent])
        dispatch(setProjectNavigation({ blockId, nodeIds }))
      } else if (mode === AppMode.fill || mode === AppMode.pro) {
        const { parent, isParent } = getParent(state, blockId, nodeId)
        const newNodeId = shift && isParent ? parent.id : nodeId
        const nodeIds = newNodeId ? [newNodeId] : []
        dispatch(setProjectNavigation({ blockId, nodeIds }))
      } else if (mode === AppMode.comment) {
        const elementId = isShellNode(node) ? node.elementId || null : null
        dispatch(setCommentForm({ blockId, elementId }))
      }
    }
    dispatch(dragEnd())
    isDragging = false
    if (raf) {
      cancelAnimationFrame(raf)
    }
    document.removeEventListener('mousemove', handleMouseMove, true)
    document.removeEventListener('mouseup', handleMouseUp, true)
  }

  if (downEvent.button === 0) {
    document.addEventListener('mousemove', handleMouseMove, true)
    document.addEventListener('mouseup', handleMouseUp, true)
  }
}
